/**
 *
 * # track events:
 * import GtmService from 'static/script/app/ui/google-tagmanager-service';
 * GtmService.trackGtmEvent("zorro", { marvel: false, black: true })
 *
 *
 *
 */

import * as Sentry from '@sentry/react';
import initData from 'init-data';
import { buildInitialGtmDataLayerFromInitData } from './google-tagmanager-helper';

/**
 * SPA page view tracking becomes enabled after first
 * page view tracking which happens after the first
 * page is loaded from server.
 * This prevents reset of data layer after a SPA route has
 * changed (happens late after full page load) and prevents
 * duplicated trackPageView events.
 */
let spaPageViewTrackingEnabled = false;

class GoogleTagManagerService {
    loadGtmScript() {
        this._newPageFromServer();

        const accountId = 'GTM-5TMB2D';
        (function(w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = '//www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', accountId);
    }

    push(payload) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
    }

    _getDatalayerObject() {
        let output = {};
        if (window.dataLayer) {
            for (let entry of window.dataLayer) {
                output = { ...output, ...entry };
            }
        }
        return output;
    }

    /**
     * destroy datalayer from previous loading. this is really only useful in spa pages, it's backwards compatible so it doesnt do any harm on old pages
     */
    _reset() {
        if (!window.dataLayer) {
            return;
        }

        let data = this._getDatalayerObject();
        for (let key of Object.keys(data)) {
            data[key] = undefined;
        }
        window.dataLayer.push({ ...data, event: 'reset' });
    }

    _hasPreviouslyTrackedPage = () =>
        !!window.dataLayer?.find(item => item.event === 'trackPageview');

    newPage(pageType, initialDataLayer) {
        if (this._hasPreviouslyTrackedPage()) {
            this._reset();
        }
        this._pushInitialDataLayer(initialDataLayer);
        this.push({ event: 'pageType', 'page.pageType': pageType });
    }

    /**
     * Same as newPage but with logic for case in Touchweb where initial
     * datalayer was set on server and already tracked on landing page.
     * This function does nothing until after first call to trackPageView
     * (e.g. after a SPA-navigation away from the landing page).
     * @param {*} pageType
     * @param {*} initialDataLayer
     * @returns
     */
    newSpaPage(pageType, initialDataLayer) {
        if (!spaPageViewTrackingEnabled) {
            return;
        }
        this.newPage(pageType, initialDataLayer);
    }

    _newPageFromServer() {
        const initialDataLayer = buildInitialGtmDataLayerFromInitData(initData);
        this._pushInitialDataLayer(initialDataLayer);
        // ------------------------------------------------
        // backwards compatibility
        // get old datalayer info and push it in
        if (window.legacyDataLayer) {
            for (let entry of window.legacyDataLayer) {
                window.dataLayer.push({
                    event: 'legacyDataLayer',
                    ...entry
                });
            }
        }
    }

    _pushInitialDataLayer(initialDataLayer) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            ...initialDataLayer,
            event: 'initialDataLayer'
        });
    }

    /**
     * tracks a google analytics event
     * @param {string} category ga category
     * @param {string} action ga action what happens, ie: "Filter box - open/close"
     * @param {string} label ga label what the value of the action was, ie "close"
     * @param {integer} [value] ga interger value of the action.
     */
    trackAction(category, action, label, value = 0, nonInteractive = false) {
        this.push({
            event: 'trackEvent',
            eventCategory: category || '',
            eventAction: action || '',
            eventLabel: label || '',
            eventValue: value || '0',
            eventNonInteractive: nonInteractive
        });

        Sentry.addBreadcrumb({
            type: 'default',
            level: Sentry.Severity.Info,
            category: nonInteractive ? 'tracking' : 'ui-action',
            message: 'Analytics Event',
            data: {
                category,
                action,
                label,
                value
            }
        });
    }

    /**
     * google tag manager event. Note: if you want to send google analytics events use trackAction instead
     * @param {string} eventName
     * @param {object} [event] data
     */
    trackGtmEvent(eventName, data = {}) {
        this.push({
            event: eventName,
            ...data
        });
    }

    /**
     * Track a pageview. Use this script in SPA's to make sure that analytics gets pageview information.
     * This creates a virtual page view since google analytics by default doesn't track changes to url via
     * push state.
     */
    trackPageView() {
        this.trackGtmEvent('trackPageview');
        spaPageViewTrackingEnabled = true;
    }

    /**
     *
     * @param {string} category
     * @param {string} action
     * @param {string} label
     * @param {string} callback
     * @param {} value
     */
    trackLinkClickAndCallback(category, action, label, callback, value = 0) {
        this.push({
            event: 'trackEvent',
            eventCategory: category || '',
            eventAction: action || '',
            eventLabel: label || '',
            eventValue: value || '0',
            eventNonInteractive: false,
            eventCallback: callback // part of google tag manager api to execute code after all tags in datalayer have been executed
        });
    }

    trackLinkClickAndGotoUrl(category, action, label, url, value = 0) {
        const callback = () => {
            location.href = url;
        };
        this.trackLinkClickAndCallback(
            category,
            action,
            label,
            callback,
            value
        );
    }
}

export default new GoogleTagManagerService();
